import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Lightweight toast system.
 *
 * Usage:
 *   <ToastProvider>
 *     <App />
 *   </ToastProvider>
 *
 *   const toast = useToast();
 *   toast.success('Saved');
 *   toast.error('Failed to save', { duration: 4000 });
 */

const ToastContext = createContext(null);

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return `tst-${Date.now()}-${idCounter}`;
};

const toneStyles = {
  success: {
    container: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    icon: 'bg-emerald-100 text-emerald-700',
    path: 'M5 13l4 4L19 7',
  },
  error: {
    container: 'bg-rose-50 border-rose-200 text-rose-900',
    icon: 'bg-rose-100 text-rose-700',
    path: 'M12 8v4m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z',
  },
  info: {
    container: 'bg-sky-50 border-sky-200 text-sky-900',
    icon: 'bg-sky-100 text-sky-700',
    path: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  warning: {
    container: 'bg-amber-50 border-amber-200 text-amber-900',
    icon: 'bg-amber-100 text-amber-700',
    path: 'M12 9v2m0 4h.01M10.29 3.86l-8 14A1 1 0 003.16 19h17.68a1 1 0 00.87-1.5l-8-14a1 1 0 00-1.74 0z',
  },
};

function ToastItem({ toast, onClose }) {
  const style = toneStyles[toast.type] || toneStyles.info;

  useEffect(() => {
    if (toast.duration === Infinity) return undefined;
    const timer = setTimeout(() => onClose(toast.id), toast.duration || 3500);
    return () => clearTimeout(timer);
  }, [toast, onClose]);

  return (
    <div
      role="status"
      className={`w-full sm:w-80 pointer-events-auto border rounded-xl shadow-medium backdrop-blur-sm px-4 py-3 animate-fade-in-up ${style.container}`}
    >
      <div className="flex items-start gap-3">
        <span className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${style.icon}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={style.path} />
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          {toast.title && <p className="text-sm font-semibold">{toast.title}</p>}
          {toast.message && <p className="text-sm leading-snug">{toast.message}</p>}
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => onClose(toast.id)}
          className="shrink-0 text-slate-500 hover:text-slate-800 p-0.5 rounded-md"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const close = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((type, message, options = {}) => {
    const id = nextId();
    const toast = {
      id,
      type,
      message: typeof message === 'string' ? message : (options.message || ''),
      title: typeof message === 'object' && message?.title ? message.title : options.title,
      duration: options.duration,
    };
    setToasts((prev) => [...prev, toast]);
    return id;
  }, []);

  const api = useMemo(
    () => ({
      show: push,
      success: (msg, opts) => push('success', msg, opts),
      error: (msg, opts) => push('error', msg, opts),
      info: (msg, opts) => push('info', msg, opts),
      warning: (msg, opts) => push('warning', msg, opts),
      dismiss: close,
    }),
    [push, close]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-0 z-[100] flex flex-col items-end justify-start gap-2 p-4 sm:p-6">
        <div className="ml-auto flex flex-col gap-2 w-full max-w-sm">
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onClose={close} />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe no-op in case the provider isn't mounted, so feature pages don't crash.
    return {
      show: () => null,
      success: () => null,
      error: () => null,
      info: () => null,
      warning: () => null,
      dismiss: () => {},
    };
  }
  return ctx;
}

export default ToastProvider;
